import { useEffect, useState } from 'react';
import { Col, Row, Button, Card } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

//Context
import { useAuth } from '../../../../context/auth';

//API
import tipoDeFiscalizacao_api from '../../../../api/tipoDeFIscalizacao_api';
import infracoes_api from '../../../../api/infracoes_api';
import usuario_api from '../../../../api/usuario_api';
import relatorio_api from '../../../../api/relatorio_api';

//Components
import TextField from '../../../../components/TextField';
import RelatorioInfracoes from './RelatorioInfracoes';

//Helpers
import { showMessage } from '../../../../helpers/message';
import mascaras from '../../../../helpers/mascaras';
import { objCampo, validarCampos, isDateOverflow } from '../../../../helpers/ValidadorDeCampos';
import { criarOnChangeMultiSelect } from '../../../../helpers/onChangeMultiSelect';

function initialStateFiltroInfracoesRelatorio() {
  const dataInicial = new Date();
  dataInicial.setMonth(dataInicial.getMonth() - 1);

  return {
    tipoFiscalizacao_Ids: [],
    infracao_Id: "",
    dataInfracaoInicial: moment(dataInicial).format("YYYY-MM-DD"),
    dataInfracaoFinal: moment(new Date()).format("YYYY-MM-DD"),
    usuario_Id: "",
    nomeInfrator: "",
    situacao: "",
    numeroInfracao: "",
    periodoDatas: "Data da infração",
    cpfcnpj: ""
  }
}

export default function InfracoesRelatorio() {
  const { setLoading, session } = useAuth();

  //State para consultar infrações
  const [filtroInfracoesRelatorio, setFiltroInfracoesRelatorio] = useState(initialStateFiltroInfracoesRelatorio());

  //State para pegar lista dos tipos de fiscalizacao
  const [listTipoDeFiscalizacao, setListTipoDeFiscalizacao] = useState([]);

  //State para cards
  const [infracoesRelatorio, setInfracoesRelatorio] = useState([]);

  //State para pegar os perfil dos usuarios
  const [listUsuarios, setListUsuarios] = useState([]);

  //State para pegar os tipos de infrações
  const [infracoes, setInfracoes] = useState([]);

  //State para pegar situações
  const [situacao, setSituacao] = useState([]);

  // State para armazenar status dos boletos
  const [statusBoletos, setStatusBoletos] = useState([
    { value: "Registrado", label: "Registrado" },
    { value: "Liquidado", label: "Liquidado" },
    { value: "Vencido", label: "Vencido" },
    { value: "Cancelado", label: "Cancelado" },
  ]);

  //Estado de controle dos campos de erro
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();

  //Estado de controle dos campos obrigatorios 
  const [campos] = useState([
    objCampo({ name: "tipoFiscalizacao_Ids", required: true })
  ]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setFiltroInfracoesRelatorio({
      ...filtroInfracoesRelatorio,
      [name]: value
    })
  }

  const handleInfracoesRelatorio = async () => {
    // Validar campos obrigatórios
    const _errors = validarCampos(filtroInfracoesRelatorio, campos);
    setErrors(_errors[0]);

    if (_errors[1].length > 0) return true;

    //Data Inicial/Final são opcionais quando Nome do Infrator, Número da Infração ou CPF/CNPJ forem informados
    const possuiFiltroAlternativo = !!(filtroInfracoesRelatorio.nomeInfrator?.trim() || filtroInfracoesRelatorio.numeroInfracao?.trim() || filtroInfracoesRelatorio.cpfcnpj?.trim());

    if (!possuiFiltroAlternativo && (!filtroInfracoesRelatorio.dataInfracaoInicial || !filtroInfracoesRelatorio.dataInfracaoFinal)) {
      return showMessage("Aviso", "Informe o período de datas ou ao menos um dos filtros: Nome do Infrator, Número da Infração ou CPF/CNPJ.", "warning", null);
    }

    if (filtroInfracoesRelatorio.dataInfracaoInicial && !moment(filtroInfracoesRelatorio.dataInfracaoInicial, "YYYY-MM-DD", true).isValid()) {
      return showMessage(
        "Aviso",
        `A data início informada não é válida. Verifique o valor informado no calendário.`,
        "warning",
        null
      );
    }

    if (filtroInfracoesRelatorio.dataInfracaoFinal && !moment(filtroInfracoesRelatorio.dataInfracaoFinal, "YYYY-MM-DD", true).isValid()) {
      return showMessage(
        "Aviso",
        `A data final informada não é válida. Verifique o valor informado no calendário.`,
        "warning",
        null
      );
    }

    if (filtroInfracoesRelatorio.dataInfracaoInicial && filtroInfracoesRelatorio.dataInfracaoFinal &&
      isDateOverflow(filtroInfracoesRelatorio.dataInfracaoInicial, filtroInfracoesRelatorio.dataInfracaoFinal, 12, 'months')) {
      return showMessage("Aviso", "A pesquisa deve ter um período máximo de 12 meses!", "warning", null);
    }

    setFiltroInfracoesRelatorio({
      ...filtroInfracoesRelatorio,
      tipoFiscalizacao_Descricao: listTipoDeFiscalizacao
        .filter(v => (filtroInfracoesRelatorio.tipoFiscalizacao_Ids ?? []).includes(v.value))
        .map(v => v.label)
        .join(", "),
      infracao_nome: infracoes.find(
        v => v.value == filtroInfracoesRelatorio.infracao_Id
      )?.label ?? "",
      usuario_nome: listUsuarios.find(
        v => v.value == filtroInfracoesRelatorio.usuario_Id
      )?.label ?? "",
    });

    setLoading(true);

    try {
      const { data } = await relatorio_api.RelatorioInfracoes(filtroInfracoesRelatorio);
      const dataFormatada = data?.map(value => ({
        ...value,
        cpfcnpj: mascaras.cpfcnpjMask(value.cpfcnpj)
      }));
      setInfracoesRelatorio(dataFormatada);
    } catch (err) {
      showMessage(
        "Error",
        err?.message ?? "Error na operação.",
        "error",
        null
      );
    } finally {
      setLoading(false);
    }
  };

  //Função para campos select
  const onChangeMultiSelect = criarOnChangeMultiSelect({
    state: filtroInfracoesRelatorio,
    setState: setFiltroInfracoesRelatorio,
    campos,
    errors,
    setErrors,
    //Sem tipo selecionado não há situação possível, então o campo é limpo junto
    aoAlterar: (novoFiltro, e) =>
      e.name === 'tipoFiscalizacao_Ids' && novoFiltro.tipoFiscalizacao_Ids.length === 0
        ? { situacao: "" }
        : null
  });

  const onChangeSelect = (value, e) => {
    const _filtroInfracoesRelatorio = {
      ...filtroInfracoesRelatorio,
      [e.name]: value ? value.value.toString() : null,
    }

    //Validando erros no campo especifico para checar em tempo de digitação se ele possui algum erro
    const _campos = campos.filter(campo => campo.name == e.name);
    const fieldError = validarCampos(_filtroInfracoesRelatorio, _campos);

    const _errors = { ...errors };
    delete _errors[e.name];
    if (fieldError[1].length === 0)
      setErrors({ ..._errors });
    else
      setErrors({ ..._errors, ...fieldError[0] });

    setFiltroInfracoesRelatorio(_filtroInfracoesRelatorio);
  }
  const loadUsuarios = async () => {
    try {
      setLoading(true);
      const { data } = await usuario_api.getList();

      const list = data.map(value => ({
        ...value,
        value: value.id,
        label: value.nome
      }));

      setListUsuarios(list);
    } catch (e) {
      showMessage("Erro", e.message, "error", null);
    } finally {
      setLoading(false);
    }
  };

  const loadInfracoes = async () => {
    try {
      const { data } = await infracoes_api.getListAtivos();

      const list = data.map(value => ({
        ...value,
        value: value.id,
        label: value.identificacao
      }));

      setInfracoes(list);
    } catch (e) {
      showMessage("Erro", e.message, "error", null);
    } finally {
      setLoading(false);
    }
  };

  const loadTipoDeFiscalizacao = async () => {
    try {
      setLoading(true);
      const { data } = await tipoDeFiscalizacao_api.getList();

      const list = data.map(value => ({
        ...value,
        value: value.id,
        label: value.identificacao
      }));

      setListTipoDeFiscalizacao(list);
    } catch (err) {
      showMessage("Error", err?.message ?? "Error na operação.", "error", null);
    } finally {
      setLoading(false);
    }
  };

  //Função que carrega a união das situações de todos os tipos de fiscalização selecionados
  const loadSituacoes = async (tipoFiscalizacao_Ids) => {
    const ids = tipoFiscalizacao_Ids ?? [];

    if (ids.length === 0) {
      setSituacao([]);
      return;
    }

    try {
      setLoading(true);
      const responses = await Promise.all(ids.map(id => tipoDeFiscalizacao_api.GetSituacoes(id)));

      const todas = responses.flatMap(response => response.data ?? []);
      const _situacoes = Array.from(new Set(todas)).map(value => ({
        value,
        label: value
      }));

      const opcoes = [
        { value: "Cancelada", label: "Cancelada" },
        ..._situacoes
      ];

      setSituacao(opcoes);

      //Limpa a situação selecionada quando ela não existe mais no conjunto dos tipos escolhidos
      setFiltroInfracoesRelatorio(anterior =>
        !anterior.situacao || opcoes.some(opcao => opcao.value === anterior.situacao)
          ? anterior
          : { ...anterior, situacao: "" });
    } catch (err) {
      showMessage("Error", err?.message ?? "Error na operação.", "error", null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsuarios();
    loadInfracoes();
    loadTipoDeFiscalizacao();
  }, []);

  useEffect(() => {
    loadSituacoes(filtroInfracoesRelatorio.tipoFiscalizacao_Ids)
  }, [filtroInfracoesRelatorio.tipoFiscalizacao_Ids]);

  if (infracoesRelatorio.length > 0) {
    return <RelatorioInfracoes
      handleVoltar={() => setInfracoesRelatorio([])}
      infracoesRelatorio={infracoesRelatorio}
      filtros={filtroInfracoesRelatorio}
    />
  }

  return <Card className='shadowFicha mainContainerFicha p-4'>
    <div className='mb-3'>
      <Button color='secondary' className='text-white' onClick={() => navigate(-1)}>
        <FontAwesomeIcon icon={faArrowLeft} />
        <span className='px-2'> Voltar </span>
      </Button>
    </div>
    <h4 className='text-secondary m-0'>Relatório de Infrações</h4>
    <Row className='mt-3'>
      <Col md={12} lg={5}>
        <TextField
          type={"select"}
          name={"tipoFiscalizacao_Ids"}
          label={"* Tipo de fiscalização"}
          multiple
          options={listTipoDeFiscalizacao}
          onChange={onChangeMultiSelect}
          value={listTipoDeFiscalizacao.filter(value => (filtroInfracoesRelatorio.tipoFiscalizacao_Ids ?? []).includes(value.value))}
          invalid={!!filtroInfracoesRelatorio.numeroInfracao?.trim() ? false : !!errors.tipoFiscalizacao_Ids}
        />
      </Col>
      <Col md={12} lg={4}>
        <TextField
          type={"select"}
          name={"infracao_Id"}
          label={"Infração"}
          placeholder='TODOS'
          searchable
          options={infracoes}
          onChange={onChangeSelect}
          value={infracoes.filter(value => value.value == filtroInfracoesRelatorio.infracao_Id)}
          invalid={!!errors.infracao_Id}
        />
      </Col>
      <Col md={12} lg={3}>
        <TextField
          type={"select"}
          name={"situacao"}
          label={"Situação"}
          placeholder='TODOS'
          searchable
          options={situacao}
          value={situacao.filter(value => value.value == filtroInfracoesRelatorio.situacao)}
          onChange={onChangeSelect}
        />
      </Col>
      <Col md={12} lg={4}>
        <TextField
          type={"text"}
          name={"numeroInfracao"}
          label={"Número da infração"}
          onChange={onChange}
          value={filtroInfracoesRelatorio.numeroInfracao}
          maxLength={30}
        />
      </Col>
      <Col md={12} lg={4}>
        <TextField
          type={"text"}
          name={"nomeInfrator"}
          label={"Nome do Infrator"}
          onChange={onChange}
          value={filtroInfracoesRelatorio.nomeInfrator}
        />
      </Col>
      <Col md={12} lg={4}>
        <TextField
          name="cpfcnpj"
          label={"CPF/CNPJ"}
          value={filtroInfracoesRelatorio.cpfcnpj}
          onChange={onChange}
          mask={mascaras.cpfcnpjMask}
          maxLength={18}
        />
      </Col>
      <Col md={12} lg={4} className={session != null && session.realizaFiscalizacao ? "d-none" : ""}>
        <TextField
          type={"select"}
          name={"usuario_Id"}
          label={"Usuario"}
          searchable
          options={listUsuarios}
          onChange={onChangeSelect}
          value={listUsuarios.filter(value => value.value == filtroInfracoesRelatorio.usuario_Id)}
        />
      </Col>
      <Col md={12} lg={4} className={session != null && session.realizaFiscalizacao ? "d-none" : ""}>
        <TextField
          type={"select"}
          name={"statusBoleto"}
          label={"Status Boleto"}
          searchable
          options={statusBoletos}
          onChange={onChangeSelect}
          value={statusBoletos.filter(value => value.value == filtroInfracoesRelatorio.statusBoleto)}
        />
      </Col>
      <Col md={12} lg={4}>
        <TextField
          type={"select"}
          name={"periodoDatas"}
          label={"* Período de Datas"}
          onChange={onChange}
          options={[
            { label: "Data Lavratura", value: "Data Lavratura" },
            { label: "Data da infração", value: "Data da infração" },
          ]}
          value={filtroInfracoesRelatorio.periodoDatas}
        />
      </Col>
      <Col md={12} lg={2}>
        <TextField
          type={"date"}
          name={"dataInfracaoInicial"}
          label={"Data Inicio"}
          onChange={onChange}
          value={filtroInfracoesRelatorio.dataInfracaoInicial}
        />
      </Col>
      <Col md={12} lg={2}>
        <TextField
          type={"date"}
          name={"dataInfracaoFinal"}
          label={"Data Final"}
          onChange={onChange}
          value={filtroInfracoesRelatorio.dataInfracaoFinal}
        />
      </Col>

      <Col md={12} lg={2}>
        <Button
          className='w-100 fw-bold mt-2'
          color='info text-light'
          onClick={handleInfracoesRelatorio}>Gerar Relatório</Button>
      </Col>
    </Row>
  </Card>;
}
